import { Link } from 'react-router-dom'
import { STAEDTE_ERTRAG_PAGES } from '../../content/landingPages/programmatic/staedteErtrag'
import { NATIONAL_AVERAGE_YIELD_KWH_PER_KWP } from '../../content/landingPages/programmatic/cityYieldFacts'
import { formatKwh } from '../../utils/format'
import { useIsMobile } from '../../hooks/useIsMobile'
import { useStaedteErtragRankingSeo } from '../../seo/useStaedteErtragRankingSeo'
import { MarketingHeader } from './MarketingHeader'
import { MarketingFooter } from './MarketingFooter'
import { useMarketingHeaderActions } from './useMarketingHeaderActions'

function formatSignedPercent(value: number): string {
  const rounded = Math.round(value * 10) / 10
  const sign = rounded > 0 ? '+' : ''
  return `${sign}${rounded.toLocaleString('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} %`
}

/**
 * Ranking-Hub-Seite für die 250 Städte-Ertragsseiten (SEO-4.0-Story Task 08, siehe
 * docs/tasks/active/seo/seo-4.0/08-staedte-rollout-pilot.md) - der eigentliche Linkmagnet der Story
 * (Ranking-/Listen-Content-Format), verlinkt auf jede der 250 Detailseiten. Bespoke Page statt
 * generischer `SeoLandingPage`, weil eine 250-Zeilen-Tabelle nicht ins `LandingPageSection`-Modell
 * (heading/paragraph/list) passt - gleiches Prinzip wie `BlogListPage`/`PvRechnerPage`.
 *
 * Tabelle auf Desktop, Karten-Liste auf Mobil (`useIsMobile`, CLAUDE.md-Mobile-Richtlinie) -
 * gleiches Muster wie `CraftsmanRequestsTable`, hier aber rein informativ (keine Formulare), daher
 * deutlich einfacher.
 */
export function StaedteErtragRankingPage() {
  useStaedteErtragRankingSeo()
  const headerActions = useMarketingHeaderActions()
  const isMobile = useIsMobile()

  const ranked = [...STAEDTE_ERTRAG_PAGES]
    .filter((page) => page.cityYield)
    .sort((a, b) => a.cityYield!.rank - b.cityYield!.rank)

  return (
    <>
      <MarketingHeader {...headerActions} />
      <article className="blog-page seo-landing-page staedte-ranking-page">
        <p className="eyebrow">Städte-Ranking</p>
        <h1>Photovoltaik-Ertrag: Ranking der 250 größten deutschen Städte</h1>
        <p className="pv-rechner-intro">
          Wo lohnt sich Photovoltaik am meisten? Diese Übersicht zeigt den zu erwartenden PV-Jahresertrag pro
          kWp installierter Leistung für die 250 einwohnerstärksten deutschen Städte - unter einer festen
          Referenz-Annahme (Süd-Ausrichtung, 35° Dachneigung, PVGIS-Daten). Der bundesweite Durchschnitt
          dieser 250 Städte liegt bei {formatKwh(NATIONAL_AVERAGE_YIELD_KWH_PER_KWP)} pro kWp und Jahr. Klicken
          Sie auf eine Stadt für ein Ertragsdiagramm und weitere Details.
        </p>

        {isMobile ? (
          <div className="request-cards staedte-ranking-cards">
            {ranked.map((page) => {
              const y = page.cityYield!
              return (
                <Link key={page.slug} to={`/${page.slug}`} className="dashboard-card staedte-ranking-card">
                  <dl className="dashboard-card-stats">
                    <div className="dashboard-card-stat">
                      <dt>Rang</dt>
                      <dd>{y.rank}</dd>
                    </div>
                    <div className="dashboard-card-stat">
                      <dt>Stadt</dt>
                      <dd>{y.cityName}</dd>
                    </div>
                    <div className="dashboard-card-stat">
                      <dt>Bundesland</dt>
                      <dd>{y.bundesland}</dd>
                    </div>
                    <div className="dashboard-card-stat">
                      <dt>Ertrag/kWp</dt>
                      <dd>{formatKwh(y.annualYieldKwhPerKwp)}</dd>
                    </div>
                    <div className="dashboard-card-stat">
                      <dt>ggü. Schnitt</dt>
                      <dd>
                        {formatSignedPercent(
                          ((y.annualYieldKwhPerKwp - y.nationalAverageKwhPerKwp) / y.nationalAverageKwhPerKwp) * 100,
                        )}
                      </dd>
                    </div>
                  </dl>
                </Link>
              )
            })}
          </div>
        ) : (
          <div className="requests-table-scroll">
            <table className="requests-table staedte-ranking-table">
              <thead>
                <tr>
                  <th>Rang</th>
                  <th>Stadt</th>
                  <th>Bundesland</th>
                  <th>Ertrag pro kWp/Jahr</th>
                  <th>ggü. Bundesdurchschnitt</th>
                </tr>
              </thead>
              <tbody>
                {ranked.map((page) => {
                  const y = page.cityYield!
                  const deltaPercent = ((y.annualYieldKwhPerKwp - y.nationalAverageKwhPerKwp) / y.nationalAverageKwhPerKwp) * 100
                  return (
                    <tr key={page.slug}>
                      <td>{y.rank}</td>
                      <td>
                        <Link to={`/${page.slug}`}>{y.cityName}</Link>
                      </td>
                      <td>{y.bundesland}</td>
                      <td>{formatKwh(y.annualYieldKwhPerKwp)}</td>
                      <td>{formatSignedPercent(deltaPercent)}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}

        <p className="staedte-ranking-methodik-note">
          Methodik: Ertragswerte basieren auf PVGIS (Photovoltaic Geographical Information System der
          EU-Kommission) unter der Referenz-Annahme Süd-Ausrichtung/35° Dachneigung - Richtwerte, keine Zusage
          für ein konkretes Gebäude. Details zur Berechnung und zum Einfluss der tatsächlichen Dachausrichtung
          in der <Link to="/methodik-preisberechnung">Methodik</Link>. Eine dachspezifische Berechnung liefert
          der kostenlose <Link to="/pv-rechner">PV-Rechner</Link>.
        </p>

        <p className="blog-post-cta seo-landing-cta">
          <button type="button" className="btn-primary" onClick={() => headerActions.onSelectCustomer()}>
            Jetzt Anfrage erstellen
          </button>
          <Link to="/pv-rechner" className="btn-secondary">
            PV-Rechner öffnen
          </Link>
        </p>
      </article>
      <MarketingFooter />
    </>
  )
}
